import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Mail, MessageCircle, Clock, Send, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Reveal } from "@/components/Reveal";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DISCORD_URL = "https://discord.gg/fkm3rwvHVB";

const QUICK_FAQS = [
  ["Where do I get my Discord access?",
   "The moment you purchase any challenge, your Discord invite is delivered instantly with your account details. Free mentorship starts immediately — pass, fail, or re-take, access stays yours."],
  ["How fast are payouts processed?",
   "Two-Step and One-Step funded accounts can request payouts every 5 days; Instant Funding and Instant Light accounts every 14 days (Light accounts are subject to the 25% Consistency Rule). KYC is completed once, at your first payout."],
  ["When do I get my login details?",
   "Immediately after checkout. Your Match-Trader credentials and Discord invite land in your inbox within minutes — check spam if you don't see them, or message us below."],
];

const CARDS = [
  {
    icon: Mail,
    title: "Official Support Email",
    desc: "All inquiries and site emails are routed directly here.",
    action: { label: "admin@myfxpocket.com", href: "mailto:admin@myfxpocket.com" },
    testId: "contact-card-email",
  },
  {
    icon: MessageCircle,
    title: "Discord Support",
    desc: "Join the MyFXPocket Traders Discord for instant live support.",
    action: { label: "Join the Discord", href: DISCORD_URL },
    testId: "contact-card-discord",
  },
  {
    icon: Clock,
    title: "Support Hours",
    desc: "Markets never sleep — neither do we.",
    action: { label: "24/7 Trader Support", href: null },
    testId: "contact-card-hours",
  },
];

const newQuestion = () => {
  const a = 2 + Math.floor(Math.random() * 8);
  const b = 1 + Math.floor(Math.random() * 9);
  return Math.random() > 0.5 ? { a, b, op: "+", ans: a + b } : { a: a + b, b, op: "−", ans: a };
};

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "general", message: "", website: "" });
  const [q, setQ] = useState(newQuestion);
  const [answer, setAnswer] = useState("");
  const [sending, setSending] = useState(false);
  const [captchaError, setCaptchaError] = useState(false);

  const captchaOk = useMemo(() => parseInt(answer, 10) === q.ans, [answer, q]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!captchaOk) {
      setCaptchaError(true);
      setQ(newQuestion());
      setAnswer("");
      return;
    }
    setCaptchaError(false);
    setSending(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Send failed");
      toast.success("Message sent — our team will get back to you shortly.");
      setForm({ name: "", email: "", subject: "general", message: "", website: "" });
    } catch (err) {
      toast.error(err.message || "Could not send your message. Please email admin@myfxpocket.com directly.");
    } finally {
      setSending(false);
      setQ(newQuestion());
      setAnswer("");
    }
  };

  const inputCls =
    "w-full rounded-xl bg-[#030B16]/70 border border-white/10 px-4 py-3.5 text-white text-sm placeholder:text-[#999999]/40 focus:outline-none focus:border-[#FFD700]/60 focus:ring-1 focus:ring-[#FFD700]/30 transition-all";

  return (
    <main className="pt-32 pb-24 lg:pb-32" data-testid="contact-page">
      <Seo
        title="Contact Us — MyFXPocket Trader Support"
        description="Questions about challenges, payouts or Discord access? Reach the MyFXPocket support team by email, live chat or Discord — 24/7 trader support."
        path="/contact"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="text-center flex flex-col items-center">
          <div className="flex items-center gap-4 mb-6">
            <span className="eyebrow">Contact</span>
            <span className="h-px w-10 bg-gradient-to-r from-[#FFD700] to-transparent" />
            <span className="text-xs font-semibold uppercase tracking-[0.22em] text-[#999999]">24/7 Support</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-[-0.06em] text-white leading-[1.05]">
            Get in Touch with <span className="gold-text">MyFXPocket</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-[#999999] max-w-xl">
            Have questions about our evaluations, Discord community, or payouts? Our team is here to help.
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
          {CARDS.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.08}>
              <div className="glass-card rounded-2xl p-6 h-full" data-testid={c.testId}>
                <div className="w-11 h-11 rounded-xl bg-[#FFD700]/10 border border-[#FFD700]/25 flex items-center justify-center mb-4">
                  <c.icon size={20} className="text-[#FFD700]" />
                </div>
                <h3 className="text-lg font-semibold tracking-[-0.02em] text-white">{c.title}</h3>
                <p className="text-sm text-[#999999] mt-1.5 leading-relaxed">{c.desc}</p>
                {c.action.href ? (
                  <a
                    href={c.action.href}
                    target={c.action.href.startsWith("http") ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    className="inline-block mt-3 text-sm font-semibold text-[#FFD700] hover:underline"
                  >
                    {c.action.label}
                  </a>
                ) : (
                  <div className="mt-3 text-sm font-semibold text-[#FFD700]">{c.action.label}</div>
                )}
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-16 grid lg:grid-cols-5 gap-10 items-start">
          <Reveal delay={0.1} className="lg:col-span-3">
            <form onSubmit={submit} className="glass-card rounded-2xl p-6 sm:p-8" data-testid="contact-form">
              <h2 className="text-xl sm:text-2xl font-semibold tracking-[-0.03em] text-white mb-7">
                Send us a message
              </h2>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="cf-name" className="text-xs font-semibold uppercase tracking-[0.15em] text-[#999999] block mb-2">Full Name</label>
                  <input id="cf-name" data-testid="contact-input-name" required minLength={2} value={form.name} onChange={set("name")} placeholder="Jane Trader" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="cf-email" className="text-xs font-semibold uppercase tracking-[0.15em] text-[#999999] block mb-2">Email Address</label>
                  <input id="cf-email" data-testid="contact-input-email" type="email" required value={form.email} onChange={set("email")} placeholder="jane@email.com" className={inputCls} />
                </div>
              </div>
              <div className="mt-5">
                <label htmlFor="cf-subject" className="text-xs font-semibold uppercase tracking-[0.15em] text-[#999999] block mb-2">Subject</label>
                <select id="cf-subject" data-testid="contact-input-subject" value={form.subject} onChange={set("subject")} className={`${inputCls} appearance-none cursor-pointer`}>
                  <option value="general">General Query</option>
                  <option value="challenge">Challenge Support</option>
                  <option value="payouts">Payouts</option>
                  <option value="discord">Discord Access</option>
                </select>
              </div>
              <div className="mt-5">
                <label htmlFor="cf-message" className="text-xs font-semibold uppercase tracking-[0.15em] text-[#999999] block mb-2">Message</label>
                <textarea id="cf-message" data-testid="contact-input-message" required minLength={10} rows={5} value={form.message} onChange={set("message")} placeholder="How can we help?" className={`${inputCls} resize-y`} />
              </div>
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={set("website")}
                className="hidden"
                aria-hidden="true"
              />
              <div className="mt-5">
                <label htmlFor="cf-captcha" className="text-xs font-semibold uppercase tracking-[0.15em] text-[#999999] block mb-2">
                  Prove you're human
                </label>
                <div className="flex items-center gap-3">
                  <div className="rounded-xl bg-[#030B16]/70 border border-[#FFD700]/25 px-5 py-3.5 text-white font-bold tracking-widest whitespace-nowrap" data-testid="contact-captcha-question">
                    {q.a} {q.op} {q.b} = ?
                  </div>
                  <input
                    id="cf-captcha"
                    data-testid="contact-input-captcha"
                    type="number"
                    required
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    placeholder="Answer"
                    className={`${inputCls} max-w-[140px] ${captchaError ? "border-[#EF4444]/60" : ""}`}
                  />
                  <button
                    type="button"
                    data-testid="contact-captcha-refresh"
                    onClick={() => { setQ(newQuestion()); setAnswer(""); setCaptchaError(false); }}
                    className="p-3 rounded-xl border border-white/10 text-[#999999] hover:text-[#FFD700] hover:border-[#FFD700]/40 transition-colors"
                    aria-label="New question"
                  >
                    <RefreshCw size={16} />
                  </button>
                </div>
                {captchaError && (
                  <p className="mt-2 text-xs text-[#EF4444]" data-testid="contact-captcha-error">
                    Incorrect answer — try the new question above.
                  </p>
                )}
              </div>
              <button
                type="submit"
                data-testid="contact-submit-button"
                disabled={sending}
                className="btn-gold group w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-4 text-base font-bold mt-8 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {sending ? "Sending…" : "Send Message"}
                <Send size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <p className="mt-4 text-center text-xs text-[#999999]">
                Your message goes straight to admin@myfxpocket.com
              </p>
            </form>
          </Reveal>

          <Reveal delay={0.2} className="lg:col-span-2">
            <div className="glass-card rounded-2xl p-6 sm:p-8">
              <h2 className="text-lg font-semibold tracking-[-0.02em] text-white mb-5">Quick answers</h2>
              <Accordion type="single" collapsible className="space-y-3">
                {QUICK_FAQS.map(([qq, aa], i) => (
                  <AccordionItem
                    key={i}
                    value={`qf-${i}`}
                    data-testid={`contact-quick-faq-${i}`}
                    className="rounded-xl border border-white/5 bg-[#030B16]/50 px-4 overflow-hidden"
                  >
                    <AccordionTrigger className="text-left text-sm font-semibold text-white hover:text-[#FFD700] hover:no-underline py-4 [&[data-state=open]]:text-[#FFD700]">
                      {qq}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-[#999999] leading-relaxed pb-4">
                      {aa}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.15} className="mt-16">
          <div className="glass-card rounded-2xl p-6 sm:p-8 max-w-4xl mx-auto">
            <h2 className="text-lg sm:text-xl font-semibold tracking-[-0.02em] text-white mb-4">
              Talk to a real trading team
            </h2>
            <div className="space-y-4 text-sm sm:text-base text-[#999999] leading-relaxed">
              <p>
                Whether you're choosing between a Two-Step evaluation, a One-Step challenge, a Swing
                account or Instant Funding, our support team can walk you through profit targets,
                drawdown rules, leverage, payout schedules and add-ons before you buy. Traders with
                active accounts can get help with Match-Trader platform access, rule clarifications,
                consistency checks and payout timing.
              </p>
              <p>
                For the fastest answer, join the MyFXPocket Traders Discord — working prop traders
                and support staff are online around the clock with live market analysis and weekly
                trading sessions. For account-specific questions, email admin@myfxpocket.com or use
                the form on this page and we'll reply as quickly as possible.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </main>
  );
}
